/* Collection Infinite Scroll + Scroll Memory
   - Appends the next page's products as the user nears the bottom.
   - Saves grid HTML, scroll position, and next-page pointer to sessionStorage
     when the user clicks through to a PDP (or leaves the page).
   - Restores that state on return so the shopper lands back where they left off.
   Scoped to collection / search / tag pages that render #product-grid. */
(function () {
  const grid = document.querySelector('#product-grid');
  if (!grid) return;

  const paginationWrapper = document.querySelector('.pagination-wrapper');
  const STATE_KEY = 'deserveCollectionState';
  const pageUrl = location.pathname + location.search;

  // --- Determine initial "next page" URL ------------------------------------
  let currentPage = paginationWrapper
    ? parseInt(paginationWrapper.getAttribute('data-page'), 10) || 1
    : 1;

  function detectHasMore(wrapper, page) {
    if (!wrapper) return false;
    const links = wrapper.querySelectorAll('a[href]');
    for (const a of links) {
      const match = a.getAttribute('href').match(/[?&]page=(\d+)/);
      if (match && parseInt(match[1], 10) > page) return true;
    }
    return false;
  }

  let hasMore = detectHasMore(paginationWrapper, currentPage);

  // Hide the numeric pagination UI — infinite scroll replaces it.
  if (paginationWrapper) paginationWrapper.style.display = 'none';

  // --- Restore saved state if returning to the same collection URL ---------
  let restored = false;
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    if (raw) {
      const state = JSON.parse(raw);
      if (state && state.url === pageUrl && state.gridHTML) {
        grid.innerHTML = state.gridHTML;
        currentPage = state.currentPage || currentPage;
        hasMore = !!state.hasMore;
        restored = true;
        // Restore scroll after layout settles.
        const y = state.scrollY || 0;
        const restoreScroll = () => window.scrollTo(0, y);
        requestAnimationFrame(() => {
          restoreScroll();
          // Images may shift layout as they load — re-apply once.
          setTimeout(restoreScroll, 50);
          setTimeout(restoreScroll, 250);
        });
      }
    }
  } catch (e) {}

  // Disable browser auto-restore so ours wins on full reloads.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  // --- Sentinel for intersection-based loading -----------------------------
  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.className = 'collection-infinite__sentinel';
  grid.parentNode.insertBefore(sentinel, grid.nextSibling);

  let loading = false;

  async function loadMore() {
    if (loading || !hasMore) return;
    loading = true;
    sentinel.classList.add('is-loading');

    const nextPage = currentPage + 1;
    const url = new URL(location.href);
    url.searchParams.set('page', String(nextPage));

    try {
      const res = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('Bad response');
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = doc.querySelector('#product-grid');
      const items = newGrid ? Array.from(newGrid.children) : [];

      if (items.length === 0) {
        hasMore = false;
      } else {
        const frag = document.createDocumentFragment();
        items.forEach((el) => frag.appendChild(el));
        grid.appendChild(frag);
        currentPage = nextPage;

        const nextPagination = doc.querySelector('.pagination-wrapper');
        hasMore = detectHasMore(nextPagination, currentPage);
      }
    } catch (e) {
      console.warn('Infinite scroll fetch failed', e);
    } finally {
      loading = false;
      sentinel.classList.remove('is-loading');
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) if (entry.isIntersecting) loadMore();
    },
    { rootMargin: '800px 0px' }
  );
  observer.observe(sentinel);

  // --- State persistence ---------------------------------------------------
  const saveState = () => {
    try {
      sessionStorage.setItem(
        STATE_KEY,
        JSON.stringify({
          url: pageUrl,
          gridHTML: grid.innerHTML,
          scrollY: window.scrollY,
          currentPage,
          hasMore,
        })
      );
    } catch (e) {}
  };

  // Save when user clicks a product link (heading to PDP) …
  grid.addEventListener(
    'click',
    (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (link) saveState();
    },
    true
  );
  // … and as a fallback, on pagehide (covers middle-click, keyboard nav, etc.).
  window.addEventListener('pagehide', saveState);

  // If the current page URL changes due to facet apply, clear stale state.
  window.addEventListener('beforeunload', () => {
    if (!restored) saveState();
  });
})();
